import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PenLine, X, Save, PlusCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { RE_EDIT_SCHEMAS, getReeditSchema } from "@/lib/reeditSchemas";

/**
 * EditSectionModal — Phase 7.3.
 *
 * Renders every field defined in `RE_EDIT_SCHEMAS[form_type]` as a proper
 * input (text / textarea / number / date / time / select / checkbox / rows).
 * On save, PATCHes `/api/requisitions/{ref}/edit` with a properly-typed
 * `form_data` object (unchanged endpoint) + mandatory `re_edit_remark`.
 *
 * DEPT/HOD dropdowns are wired live from `/api/departments` and `/api/hods`
 * so the modal shows the SAME options as the original form.
 */
export default function EditSectionModal({ open, onClose, onDone, requisition }) {
  const schema = getReeditSchema(requisition?.form_type, requisition?.form_data);
  const [values, setValues] = useState(() => ({ ...(requisition?.form_data || {}) }));
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);

  useEffect(() => {
    if (!open) return;
    setValues({ ...(requisition?.form_data || {}) });
    setRemark(""); setError("");
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requisition?.ref_code]);

  const subDeptOptions = useMemo(() => {
    const d = depts.find((x) => x.name === values.mainDept);
    return d?.sub_departments || [];
  }, [depts, values.mainDept]);

  if (!open || !requisition) return null;
  const trimmed = remark.trim();
  const remarkOk = trimmed.length >= 10;

  const setField = (k, v) => setValues((cur) => ({ ...cur, [k]: v }));

  const changedPayload = () => {
    const original = requisition.form_data || {};
    const out = {};
    for (const field of schema) {
      const k = field.key;
      if (JSON.stringify(values[k]) !== JSON.stringify(original[k])) out[k] = values[k];
    }
    return out;
  };

  const submit = async () => {
    for (const f of schema) {
      if (!f.required) continue;
      const v = values[f.key];
      if (v === undefined || v === null || String(v).trim() === "") {
        toast.error(`Please fill: ${f.label}`);
        return;
      }
    }
    if (!remarkOk) { toast.error("Re-edit remark is mandatory (min 10 chars)"); return; }
    const patch = changedPayload();
    if (Object.keys(patch).length === 0) {
      toast.error("No fields changed."); return;
    }
    setBusy(true); setError("");
    try {
      const { data } = await api.patch(`/requisitions/${requisition.ref_code}/edit`, {
        form_data: patch, re_edit_remark: trimmed,
      });
      toast.success("Requisition edited");
      onDone?.(data); onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally { setBusy(false); }
  };

  const addRow = (key, rowSchema, max = 10) => {
    const cur = Array.isArray(values[key]) ? values[key] : [];
    if (cur.length >= max) return;
    const blank = Object.fromEntries(rowSchema.map((f) => [f.key, ""]));
    if (key === "bills") blank.sr_no = String(cur.length + 1);
    if (key === "guests") blank.sl_no = cur.length + 1;
    setField(key, [...cur, blank]);
  };
  const removeRow = (key, idx) => {
    const cur = values[key] || [];
    setField(key, cur.filter((_, i) => i !== idx));
  };
  const setRowCell = (key, idx, cellKey, cellVal) => {
    const cur = values[key] || [];
    setField(key, cur.map((r, i) => (i === idx ? { ...r, [cellKey]: cellVal } : r)));
  };

  const renderInput = (f) => {
    const v = values[f.key];
    const tid = `re-edit-${f.key}`;
    if (f.type === "textarea") {
      return <textarea rows={3} className="input-vgws" value={v ?? ""}
                        onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
    }
    if (f.type === "number") {
      return <input type="number" className="input-vgws" value={v ?? ""}
                     onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
    }
    if (f.type === "date") {
      return <input type="date" className="input-vgws" value={v ?? ""}
                     onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
    }
    if (f.type === "time") {
      return <input type="time" className="input-vgws" value={v ?? ""}
                     onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
    }
    if (f.type === "checkbox") {
      return (
        <label className="inline-flex items-center gap-2 text-sm text-[#1f2320]">
          <input type="checkbox" checked={!!v}
                  onChange={(e) => setField(f.key, e.target.checked)} data-testid={tid} />
          <span>{v ? "Yes" : "No"}</span>
        </label>
      );
    }
    if (f.type === "select") {
      let opts = f.options || [];
      if (f.optionsFrom === "mainDept") opts = depts.map((d) => d.name);
      if (f.optionsFrom === "subDept") opts = subDeptOptions;
      if (f.optionsFrom === "hods") opts = hods.map((h) => h.email);
      return (
        <select className="input-vgws" value={v ?? ""}
                 onChange={(e) => setField(f.key, e.target.value)} data-testid={tid}>
          <option value="">— select —</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === "rows") {
      const rows = Array.isArray(v) ? v : [];
      return (
        <div className="rounded-md border border-[#e2d7c3] bg-white">
          <table className="w-full text-xs">
            <thead className="bg-[#faf5eb] text-[#5a1520]">
              <tr>
                {f.rows.map((c) => <th key={c.key} className="text-left p-1 pl-2">{c.label}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={f.rows.length + 1} className="p-2 text-[#6e7770] italic">No rows</td></tr>
              )}
              {rows.map((row, i) => (
                <tr key={i}>
                  {f.rows.map((c) => (
                    <td key={c.key} className="p-1 pl-2">
                      <input className="input-vgws py-1 text-xs"
                              value={row[c.key] ?? ""}
                              onChange={(e) => setRowCell(f.key, i, c.key, e.target.value)}
                              data-testid={`re-edit-${f.key}-${i}-${c.key}`} />
                    </td>
                  ))}
                  <td className="p-1">
                    <button type="button" className="row-del" onClick={() => removeRow(f.key, i)}
                            data-testid={`re-edit-${f.key}-remove-${i}`}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-1 border-t border-[#e2d7c3]">
            <button type="button" className="btn-ghost text-xs"
                    onClick={() => addRow(f.key, f.rows, 10)}
                    data-testid={`re-edit-${f.key}-add`}>
              <PlusCircle size={12} /> Add row
            </button>
          </div>
        </div>
      );
    }
    return <input className="input-vgws" value={v ?? ""}
                   onChange={(e) => setField(f.key, e.target.value)} data-testid={tid} />;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         onClick={onClose} data-testid="re-edit-modal">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden bg-white rounded-xl shadow-2xl border border-[#e2d7c3] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-[#faf5eb]">
          <div className="flex items-center gap-2">
            <PenLine className="text-[#7a1f2b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Re-edit requisition — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="text-xs text-[#4a524c] bg-[#faf5eb] px-3 py-2 rounded border-l-4 border-[#b48a45]">
            Edit any field below. A snapshot of the previous version is kept
            and every touched actor is notified.
          </div>
          {error && <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded">{error}</div>}

          <div className="grid sm:grid-cols-2 gap-3">
            {schema.map((f) => (
              <div key={f.key} className={f.type === "rows" || f.type === "textarea" ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-semibold text-[#4a524c] mb-1">
                  {f.label}{f.required && <span className="text-[#b3261e]"> *</span>}
                </label>
                {renderInput(f)}
              </div>
            ))}
          </div>

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Re-edit remark <span className="text-[#b3261e]">*</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">min 10 chars</span>
            </span>
            <textarea rows={3} value={remark} onChange={(e) => setRemark(e.target.value.slice(0, 1000))}
                      placeholder="Describe what changed and why…"
                      className={`input-vgws ${trimmed && !remarkOk ? "!border-[#b3261e]" : ""}`}
                      data-testid="re-edit-remark" />
            <div className="mt-1 text-xs text-[#6e7770]">{trimmed.length}/1000</div>
          </label>
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={!remarkOk || busy}
                  className={`btn-vgws ${(!remarkOk || busy) ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="re-edit-save">
            <Save size={14} /> {busy ? "Saving…" : "Save re-edit"}
          </button>
        </div>
      </div>
    </div>
  );
}
